import { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';
import { createPortal } from 'react-dom';
import { guestService } from 'services/api';
import styles from './QrScanner.module.css';

// ── Escáner de QR — check-in automático con la cámara ──────────────────────────
export default function QrScanner({ eventId, onCheckedIn, onClose }) {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(document.createElement('canvas'));
  const cooldownRef = useRef(false); // evita reprocesar el mismo QR en cada frame mientras sigue en cuadro
  const [cameraError, setCameraError] = useState(false);
  const [feedback,    setFeedback]    = useState(null); // { type:'success'|'warn'|'error', text }

  useEffect(() => {
    let stream = null;
    let rafId  = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const video = videoRef.current;
      if (video && video.readyState === video.HAVE_ENOUGH_DATA && !cooldownRef.current) {
        const canvas = canvasRef.current;
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) handleDecoded(code.data);
      }
      rafId = requestAnimationFrame(tick);
    };

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then(s => {
        if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
        stream = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); }
        rafId = requestAnimationFrame(tick);
      })
      .catch(() => setCameraError(true));

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, []); // eslint-disable-line

  const handleDecoded = async (raw) => {
    cooldownRef.current = true;
    try {
      const payload = JSON.parse(raw);
      if (!payload.guestId || !payload.eventId || !payload.signature) {
        setFeedback({ type: 'error', text: 'QR inválido: no es un carnet de EVENTIFY' });
      } else if (String(payload.eventId) !== String(eventId)) {
        setFeedback({ type: 'error', text: 'Este QR es de otro evento' });
      } else {
        const { data } = await guestService.checkinByQr(payload);
        setFeedback({
          type: data.alreadyToday ? 'warn' : 'success',
          text: data.alreadyToday
            ? `${data.guest?.name} ya estaba registrado hoy`
            : `✓ ${data.guest?.name} — check-in exitoso`,
        });
        onCheckedIn?.();
      }
    } catch (err) {
      setFeedback({ type: 'error', text: err.response?.data?.message || 'QR inválido' });
    } finally {
      // Pausa de 2.2s antes de volver a escanear — evita que el mismo QR,
      // mientras sigue frente a la cámara, se procese en bucle.
      setTimeout(() => { cooldownRef.current = false; setFeedback(null); }, 2200);
    }
  };

  return createPortal(
    <div className={`modal-overlay ${styles.fondo}`} onClick={onClose}>
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        <div className={styles.cabecera}>
          <div className={styles.titulo}>
            <i className={`fas fa-qrcode ${styles.iconoTitulo}`} />Escanear QR
          </div>
          <button onClick={onClose} className={styles.cerrar}>✕</button>
        </div>
        <div className={styles.visor}>
          <video ref={videoRef} playsInline muted className={styles.video} />
          {cameraError && (
            <div className={styles.errorCamara}>
              No se pudo acceder a la cámara. Revisa los permisos del navegador.
            </div>
          )}
          {feedback && (
            <div className={feedback.type === 'success' ? styles.avisoOk : feedback.type === 'warn' ? styles.avisoAdvertencia : styles.avisoError}>
              {feedback.text}
            </div>
          )}
        </div>
        <div className={styles.pie}>
          Apunta la cámara al QR del carnet del invitado
        </div>
      </div>
    </div>,
    document.body
  );
}
